import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Telescope, MapPin, Sparkles, Info, Moon, Sun as SunIcon, Wind, Circle, Lock, ArrowRight } from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import axios from "axios";
import useSubscription from "@/hooks/useSubscription";
import SubscribedBadge from "@/components/earthpulse/SubscribedBadge";
import { getSubscriberEmail } from "@/lib/api";

const KIND_META = {
  moon_phase:         { icon: Moon,       label: "Moon phase",   accent: "gold" },
  solar:              { icon: SunIcon,    label: "Solar station",accent: "gold" },
  meteor_shower:      { icon: Sparkles,   label: "Meteor shower",accent: "rose" },
  comet:              { icon: Telescope,  label: "Comet",        accent: "rose" },
  eclipse:            { icon: Circle,     label: "Eclipse",      accent: "gold" },
  mercury_retrograde: { icon: Wind,       label: "Mercury Rx",   accent: "gold" },
};

const LOCATION_KEY = "ep_watch_location";
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * "Sky Events To Watch" panel — sits alongside Sky Alerts on the dashboard.
 *
 * Shows the next ~90 days of eyes-up events (new/full moons, solar
 * equinoxes/solstices, meteor showers, visible comets, eclipses) drawn
 * from GET /api/sky-events/watch. Each row expands underneath with a
 * plain-English explanation.
 *
 * Location: the user can grant browser geolocation with one tap to have
 * hemisphere-specific comets/eclipses filtered — but everything works
 * fine without it (defaults to a global view).
 */
export default function SkyEventsToWatch() {
  const { active: subActive, loading: subLoading } = useSubscription();
  const [loc, setLoc] = useState(loadLocation());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    if (!subActive) { setLoading(false); return; }   // don't fetch until unlocked
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ days: "90" });
        if (loc?.lat != null) params.set("lat", String(loc.lat));
        if (loc?.lon != null) params.set("lon", String(loc.lon));
        const em = getSubscriberEmail();
        if (em) params.set("email", em);
        const r = await axios.get(`${API}/api/sky-events/watch?${params.toString()}`);
        if (!cancelled) setEvents(r.data?.events || []);
      } catch (_) {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [subActive, loc]);

  const askLocation = () => {
    if (!("geolocation" in navigator)) return;
    setAsking(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = {
          lat: +pos.coords.latitude.toFixed(3),
          lon: +pos.coords.longitude.toFixed(3),
        };
        localStorage.setItem(LOCATION_KEY, JSON.stringify(next));
        setLoc(next);
        setAsking(false);
      },
      () => setAsking(false),
      { timeout: 8000, maximumAge: 24 * 3600 * 1000 },
    );
  };

  const grouped = useMemo(() => groupByMonth(events), [events]);

  // ── Non-subscriber locked teaser ─────────────────────────────
  if (!subLoading && !subActive) {
    return (
      <div
        className="parchment-card rounded-2xl p-8 h-full flex flex-col relative overflow-hidden"
        data-testid="sky-events-panel-locked"
      >
        <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
             style={{
               background: "radial-gradient(circle at 20% 20%, var(--gold), transparent 40%), radial-gradient(circle at 80% 60%, var(--rose-gold), transparent 40%)",
             }}
        />
        <div className="relative flex items-center justify-between mb-4 gap-4">
          <div className="label-overline flex items-center gap-2 constellation-underline">
            <Telescope size={12} /> Sky events to watch
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[color:var(--gold)]/40 bg-[color:var(--gold)]/10 text-[10px] font-medium tracking-wider uppercase text-[color:var(--gold)]">
            <Lock size={10} /> Members
          </span>
        </div>

        <div className="relative flex-1 flex flex-col justify-center items-center text-center py-8 md:py-14">
          <div className="mx-auto w-14 h-14 rounded-full border border-[color:var(--gold)]/40 flex items-center justify-center">
            <Telescope size={20} className="text-[color:var(--gold)]" />
          </div>
          <h3 className="mt-6 font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl tracking-tight max-w-md">
            The next <span className="font-serif-italic text-[color:var(--gold)]">90 days</span> of sky events — tuned to <span className="font-serif-italic text-[color:var(--gold)]">your location</span>.
          </h3>
          <p className="mt-4 font-serif-italic text-[color:var(--parchment-soft)] text-sm max-w-md leading-relaxed">
            Comets, eclipses, meteor showers, and new/full moons — with plain-English notes for each, and a one-tap "use my location" filter for the ones visible from your hemisphere.
          </p>
          <Link
            to="/subscribe"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition"
            data-testid="sky-events-subscribe"
          >
            <Sparkles size={14} /> Unlock with membership · £8.88 / mo <ArrowRight size={14} />
          </Link>
          <p className="mt-4 font-serif-italic text-[color:var(--text-muted)] text-xs">
            Included with your Earth Pulse membership · cancel anytime.
          </p>
        </div>
      </div>
    );
  }

  // ── Subscriber view ─────────────────────────────────────────
  return (
    <TooltipProvider delayDuration={150}>
      <div className="parchment-card rounded-2xl p-8 h-full flex flex-col" data-testid="sky-events-panel">
        <div className="flex items-center justify-between mb-4 gap-4">
          <div className="label-overline flex items-center gap-2 constellation-underline">
            <Telescope size={12} /> Sky events to watch
            <SubscribedBadge />
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {loc?.lat != null ? (
              <span
                className="label-overline text-[10px] text-[color:var(--gold)] inline-flex items-center gap-1"
                title={`Filtering by lat ${loc.lat}, lon ${loc.lon}`}
                data-testid="sky-events-location-active"
              >
                <MapPin size={10} /> your sky
              </span>
            ) : (
              <button
                type="button"
                onClick={askLocation}
                disabled={asking}
                className="label-overline text-[10px] text-[color:var(--text-secondary)] hover:text-[color:var(--gold)] inline-flex items-center gap-1 transition disabled:opacity-60"
                data-testid="sky-events-locate-btn"
              >
                <MapPin size={10} /> {asking ? "…" : "use my location"}
              </button>
            )}
          </div>
        </div>

        <p className="font-serif-italic text-[color:var(--text-muted)] text-xs mb-4 flex items-center gap-1.5">
          <Info size={11} /> The next 90 days — hover any event for a plain-English note underneath.
        </p>

        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="text-[color:var(--text-muted)] text-sm py-6 font-serif-italic">
              Reading the sky…
            </div>
          ) : events.length === 0 ? (
            <div className="text-[color:var(--text-muted)] text-sm py-6 font-serif-italic">
              No standout sky events in the next three months. Check back after the next new moon.
            </div>
          ) : (
            <div className="space-y-5 max-h-[420px] overflow-y-auto pr-2" data-testid="sky-events-list">
              {grouped.map(({ month, items }) => (
                <section key={month}>
                  <div className="label-overline text-[10px] mb-2 text-[color:var(--gold)]/80">
                    {month}
                  </div>
                  <ul className="space-y-2.5">
                    {items.map((e, i) => (
                      <SkyEventItem key={`${e.start}-${i}`} event={e} index={i} />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

function SkyEventItem({ event, index }) {
  const meta = KIND_META[event.kind] || { icon: Sparkles, label: event.kind, accent: "gold" };
  const Icon = meta.icon;
  const color = meta.accent === "rose" ? "var(--rose-gold)" : "var(--gold)";
  const when = new Date(event.start);
  const day = when.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const time = when.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const isAllDay = event.all_day;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <li
          className="border-l-2 pl-3 py-1 rounded-r hover:bg-[color:var(--gold)]/[0.04] cursor-help focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--gold)]/60"
          tabIndex={0}
          style={{ borderColor: color, transition: "background 200ms ease" }}
          data-testid={`sky-event-item-${index}`}
        >
          <div className="flex items-center gap-2">
            <Icon size={12} style={{ color }} />
            <span className="font-serif-italic text-xs uppercase tracking-widest" style={{ color }}>
              {day}{!isAllDay && ` · ${time}`}
            </span>
            <Info size={10} className="ml-auto text-[color:var(--text-muted)] opacity-60" aria-hidden="true" />
          </div>
          <div className="mt-1 text-[color:var(--parchment-soft)] text-sm font-serif-display tracking-tight">
            {event.title}
          </div>
          {event.description && (
            <div className="mt-0.5 font-serif-italic text-[color:var(--text-muted)] text-xs line-clamp-2">
              {event.description}
            </div>
          )}
        </li>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="start"
        sideOffset={8}
        className="max-w-md w-[min(360px,80vw)] bg-[color:var(--bg-deeper)] border border-[color:var(--gold)]/40 text-[color:var(--parchment)] font-sans-body text-sm leading-relaxed p-4 shadow-xl"
        data-testid={`sky-event-tooltip-${index}`}
      >
        <div className="label-overline mb-2 flex items-center gap-1.5" style={{ color }}>
          <Info size={10} /> What this is, in plain English
        </div>
        {event.layman && (
          <p className="font-serif-italic text-[color:var(--parchment-soft)] mb-2">
            {event.layman}
          </p>
        )}
        {event.description && (
          <p className="text-[color:var(--text-secondary)] text-xs leading-relaxed">
            {event.description}
          </p>
        )}
        {event.region && (
          <p className="mt-2 text-[10px] text-[color:var(--gold)] uppercase tracking-wider">
            Best viewed from · {event.region}
          </p>
        )}
        {event.hemisphere && event.hemisphere !== "both" && (
          <p className="mt-2 text-[10px] text-[color:var(--gold)] uppercase tracking-wider">
            Hemisphere · {event.hemisphere}
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function groupByMonth(events) {
  const groups = new Map();
  for (const e of events) {
    const d = new Date(e.start);
    const key = d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(e);
  }
  return Array.from(groups.entries()).map(([month, items]) => ({ month, items }));
}

function loadLocation() {
  try {
    const raw = localStorage.getItem(LOCATION_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p?.lat === "number" && typeof p?.lon === "number") return p;
    return null;
  } catch {
    return null;
  }
}
